import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { PriceRecord } from '../types';
import { formatDate, dateInputToUTCString } from '../utils/dateUtils';

// A price amount is valid if it is a non-empty, non-negative number. $0.00 is allowed
// (e.g. an item acquired for free); blank / NaN / negative are rejected.
const isValidAmount = (value: string): boolean => {
  if (value.trim() === '') return false;
  const n = parseFloat(value);
  return !Number.isNaN(n) && n >= 0;
};

interface PriceHistoryManagerProps {
  slideId: string;
  initialPriceHistory: PriceRecord[];
  onPriceHistoryUpdate: (priceHistory: PriceRecord[]) => void;
  className?: string;
}

export interface PriceHistoryManagerHandle {
  commitPendingChanges: () => PriceRecord[] | null;
}

const PriceHistoryManager = forwardRef<PriceHistoryManagerHandle, PriceHistoryManagerProps>(({
  slideId,
  initialPriceHistory,
  onPriceHistoryUpdate,
  className = ''
}, ref) => {
  const [priceHistory, setPriceHistory] = useState<PriceRecord[]>(initialPriceHistory || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState<string | null>(null);

  // Helper function to determine the next default type based on existing price history
  const getDefaultPriceType = (history: PriceRecord[]): 'ACQUISITION' | 'LISTING' | 'SOLD' => {
    if (history.length === 0) {
      return 'ACQUISITION';
    }

    // Check if each type exists
    const hasAcquisition = history.some(record => record.type === 'ACQUISITION');
    const hasListing = history.some(record => record.type === 'LISTING');
    const hasSold = history.some(record => record.type === 'SOLD');

    // If SOLD exists, default to SOLD (end of lifecycle)
    if (hasSold) {
      return 'SOLD';
    }

    // If LISTING exists (but not SOLD), default to SOLD (next step)
    if (hasListing) {
      return 'SOLD';
    }

    // If ACQUISITION exists (but not LISTING), default to LISTING (next step)
    if (hasAcquisition) {
      return 'LISTING';
    }

    // Fallback (shouldn't happen if history.length > 0)
    return 'ACQUISITION';
  };

  const [formData, setFormData] = useState({
    type: 'ACQUISITION' as 'ACQUISITION' | 'LISTING' | 'SOLD',
    amount: '',
    currency: 'USD',
    notes: '',
    effectiveDate: '' // Optional: if empty, backend will use current timestamp
  });

  // Sync with parent's price history when it changes
  useEffect(() => {
    setPriceHistory(initialPriceHistory || []);
  }, [initialPriceHistory]);

  // Update the default type when price history changes
  useEffect(() => {
    const defaultType = getDefaultPriceType(priceHistory);
    setFormData(prev => ({ ...prev, type: defaultType }));
  }, [priceHistory]);

  // Expose method to parent to commit any pending changes
  useImperativeHandle(ref, () => ({
    commitPendingChanges: () => {
      // Check if add form is visible and has valid uncommitted data
      if (showAddForm && isValidAmount(formData.amount)) {
        const amount = parseFloat(formData.amount);

        const now = new Date();
        const effectiveDate = formData.effectiveDate
          ? dateInputToUTCString(formData.effectiveDate)
          : now.toISOString();

        // Temporary ID for local UI tracking — the server will replace it
        // with a UUIDv7 when it detects this ID doesn't match any existing record.
        const newRecord: PriceRecord = {
          id: `temp-${crypto.randomUUID()}`,
          type: formData.type,
          amount,
          currency: formData.currency,
          recordedAt: now.toISOString(),
          effectiveDate,
          notes: formData.notes || undefined
        };

        const updatedHistory = [newRecord, ...priceHistory];

        // Update local state and notify parent
        setPriceHistory(updatedHistory);
        onPriceHistoryUpdate(updatedHistory);
        setShowAddForm(false);
        resetForm();

        // Return the updated array for immediate use by parent
        return updatedHistory;
      }

      // Check if edit form is visible and has valid uncommitted data
      if (editingRecordId && isValidAmount(formData.amount)) {
        const amount = parseFloat(formData.amount);

        const effectiveDate = formData.effectiveDate
          ? dateInputToUTCString(formData.effectiveDate)
          : new Date().toISOString();

        // Find the existing record to preserve recordedAt
        const existingRecord = priceHistory.find(r => r.id === editingRecordId);

        // Update the record in the list
        const updatedHistory = priceHistory.map(record =>
          record.id === editingRecordId
            ? {
                ...record,
                type: formData.type,
                amount,
                currency: formData.currency,
                notes: formData.notes || undefined,
                effectiveDate,
                recordedAt: existingRecord?.recordedAt || record.recordedAt
              }
            : record
        );

        // Re-sort by recordedAt
        const sortedHistory = updatedHistory.sort((a, b) =>
          new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
        );

        // Update local state and notify parent
        setPriceHistory(sortedHistory);
        onPriceHistoryUpdate(sortedHistory);
        setEditingRecordId(null);
        resetForm();

        // Return the updated array for immediate use by parent
        return sortedHistory;
      }

      return null; // No pending changes
    }
  }));

  const handleAddPrice = () => {
    const amount = parseFloat(formData.amount);
    if (!isValidAmount(formData.amount)) {
      return;
    }

    const now = new Date();
    const effectiveDate = formData.effectiveDate
      ? dateInputToUTCString(formData.effectiveDate)
      : now.toISOString();

    // Temporary ID for local UI tracking — the server will replace it
    // with a UUIDv7 when it detects this ID doesn't match any existing record.
    const newRecord: PriceRecord = {
      id: `temp-${crypto.randomUUID()}`,
      type: formData.type,
      amount,
      currency: formData.currency,
      recordedAt: now.toISOString(),
      effectiveDate,
      notes: formData.notes || undefined
    };

    const updatedHistory = [newRecord, ...priceHistory];
    setPriceHistory(updatedHistory);
    onPriceHistoryUpdate(updatedHistory);
    setShowAddForm(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      type: getDefaultPriceType(priceHistory),
      amount: '',
      currency: 'USD',
      notes: '',
      effectiveDate: ''
    });
  };

  const startEdit = (record: PriceRecord) => {
    setEditingRecordId(record.id);
    setShowAddForm(false); // Close add form if open

    // Parse the effectiveDate to date-only format
    let effectiveDateValue = '';
    if (record.effectiveDate) {
      try {
        const date = new Date(record.effectiveDate);
        // Format as YYYY-MM-DD for date input
        effectiveDateValue = date.toISOString().slice(0, 10);
      } catch (e) {
        console.error('Error parsing effectiveDate:', e);
      }
    }

    setFormData({
      type: record.type,
      amount: record.amount.toFixed(2),
      currency: record.currency,
      notes: record.notes || '',
      effectiveDate: effectiveDateValue
    });
  };

  const cancelEdit = () => {
    setEditingRecordId(null);
    resetForm();
  };

  const handleEditPrice = () => {
    if (!editingRecordId) return;

    const amount = parseFloat(formData.amount);
    if (!isValidAmount(formData.amount)) {
      return;
    }

    // If user cleared the date, default to current date (backend requires effectiveDate)
    const effectiveDate = formData.effectiveDate
      ? dateInputToUTCString(formData.effectiveDate)
      : new Date().toISOString();

    // Find the existing record to preserve recordedAt
    const existingRecord = priceHistory.find(r => r.id === editingRecordId);

    // Update the record in the list
    const updatedHistory = priceHistory.map(record =>
      record.id === editingRecordId
        ? {
            ...record,
            type: formData.type,
            amount,
            currency: formData.currency,
            notes: formData.notes || undefined,
            effectiveDate,
            recordedAt: existingRecord?.recordedAt || record.recordedAt
          }
        : record
    );

    // Re-sort by recordedAt
    const sortedHistory = updatedHistory.sort((a, b) =>
      new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
    );

    setPriceHistory(sortedHistory);
    onPriceHistoryUpdate(sortedHistory);
    setEditingRecordId(null);
    resetForm();
  };

  const formatCurrency = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (error) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'ACQUISITION':
        return { bg: 'var(--bg-accent)', text: 'var(--primary-light)', border: 'var(--primary-light)' };
      case 'LISTING':
        return { bg: 'var(--success-bg)', text: 'var(--success-dark)', border: 'var(--success)' };
      case 'SOLD':
        return { bg: 'var(--success)', text: 'white', border: 'var(--success)' };
      default:
        return { bg: 'var(--bg-primary)', text: 'var(--text-accent)', border: 'var(--border-light)' };
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'ACQUISITION': return 'Acquisition';
      case 'LISTING': return 'Listing';
      case 'SOLD': return 'Sold';
      default: return type;
    }
  };

  return (
    <div className={className}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>Price History ({priceHistory.length})</h3>
        {!editingRecordId && (
          <button
            type="button"
            onClick={() => {
              setShowAddForm(!showAddForm);
              if (!showAddForm) {
                resetForm();
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: showAddForm ? 'var(--error-bg)' : 'var(--bg-card)',
              border: '1px solid ' + (showAddForm ? 'var(--error)' : 'var(--border-medium)'),
              color: showAddForm ? 'var(--error-text)' : 'var(--text-primary)',
              fontSize: '14px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            {showAddForm ? 'Cancel' : 'Add Price'}
          </button>
        )}
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          border: '1px solid var(--border-light)',
          borderRadius: '4px',
          backgroundColor: 'var(--bg-secondary)'
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '12px',
            marginTop: 0
          }}>
            Add New Price Record
          </h4>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  type: e.target.value as any
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              >
                <option value="ACQUISITION">Acquisition</option>
                <option value="LISTING">Listing</option>
                <option value="SOLD">Sold</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Amount
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.amount}
                onChange={(e) => {
                  // Only allow numbers and decimal point
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setFormData({ ...formData, amount: value });
                  }
                }}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Notes (optional)
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Effective Date (optional)
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-input"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  style={{ flex: 1, minWidth: 0, boxSizing: 'border-box' }}
                />
                {formData.effectiveDate && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, effectiveDate: '' }))}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      flexShrink: 0
                    }}
                    aria-label="Clear effective date"
                  >
                    &times;
                  </button>
                )}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                marginTop: '4px'
              }}>
                Leave blank to use current date
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleAddPrice}
              disabled={!isValidAmount(formData.amount)}
              style={{
                flex: '1 1 120px',
                padding: '10px 16px',
                backgroundColor: (!isValidAmount(formData.amount)) ? 'var(--disabled-bg)' : 'var(--primary)',
                color: (!isValidAmount(formData.amount)) ? 'var(--disabled)' : 'var(--text-inverse)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '4px',
                border: 'none',
                cursor: (!isValidAmount(formData.amount)) ? 'not-allowed' : 'pointer'
              }}
            >
              Add Price
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                resetForm();
              }}
              style={{
                flex: '1 1 120px',
                padding: '10px 16px',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-medium)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingRecordId && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          border: '1px solid var(--border-light)',
          borderRadius: '4px',
          backgroundColor: 'var(--bg-secondary)'
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '12px',
            marginTop: 0
          }}>
            Edit Price Record
          </h4>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '12px'
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  type: e.target.value as any
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              >
                <option value="ACQUISITION">Acquisition</option>
                <option value="LISTING">Listing</option>
                <option value="SOLD">Sold</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Amount
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.amount}
                onChange={(e) => {
                  // Only allow numbers and decimal point
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setFormData({ ...formData, amount: value });
                  }
                }}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="CAD">CAD</option>
              </select>
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Notes (optional)
              </label>
              <input
                type="text"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--border-light)',
                  borderRadius: '4px',
                  fontSize: '16px'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: '4px',
                fontSize: '14px',
                fontWeight: 500
              }}>
                Effective Date (optional)
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-input"
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, effectiveDate: e.target.value }))}
                  style={{ flex: 1, minWidth: 0, boxSizing: 'border-box' }}
                />
                {formData.effectiveDate && (
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, effectiveDate: '' }))}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-secondary)',
                      fontSize: '18px',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      flexShrink: 0
                    }}
                    aria-label="Clear effective date"
                  >
                    &times;
                  </button>
                )}
              </div>
              <div style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                marginTop: '4px'
              }}>
                Leave blank to keep current date
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleEditPrice}
              disabled={!isValidAmount(formData.amount)}
              style={{
                flex: '1 1 120px',
                padding: '10px 16px',
                backgroundColor: (!isValidAmount(formData.amount)) ? 'var(--disabled-bg)' : 'var(--primary)',
                color: (!isValidAmount(formData.amount)) ? 'var(--disabled)' : 'var(--text-inverse)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '4px',
                border: 'none',
                cursor: (!isValidAmount(formData.amount)) ? 'not-allowed' : 'pointer'
              }}
            >
              Update Price
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              style={{
                flex: '1 1 120px',
                padding: '10px 16px',
                backgroundColor: 'transparent',
                border: '1px solid var(--border-medium)',
                color: 'var(--text-primary)',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Price History List */}
      <div>
        {priceHistory.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {priceHistory.map((record) => {
              const typeColors = getTypeColor(record.type);
              return (
                <div
                  key={record.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    padding: '12px',
                    border: '1px solid var(--border-light)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-card)'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '4px'
                    }}>
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: typeColors.bg,
                        color: typeColors.text,
                        border: `1px solid ${typeColors.border}`,
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500
                      }}>
                        {getTypeLabel(record.type)}
                      </span>
                      <span style={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: 'var(--text-primary)'
                      }}>
                        {formatCurrency(record.amount, record.currency)}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      marginBottom: record.notes ? '4px' : 0
                    }}>
                      {formatDate(record.effectiveDate)}
                    </div>
                    {record.notes && (
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--text-primary)'
                      }}>
                        {record.notes}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginLeft: '8px' }}>
                    <button
                      type="button"
                      onClick={() => startEdit(record)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-primary)',
                        fontSize: '14px',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        lineHeight: 1,
                        fontWeight: 500
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--text-primary)';
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--text-primary)';
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                      aria-label="Edit price record"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!window.confirm('Are you sure you want to delete this price record?')) {
                          return;
                        }
                        const updatedHistory = priceHistory.filter(r => r.id !== record.id);
                        setPriceHistory(updatedHistory);
                        onPriceHistoryUpdate(updatedHistory);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--error-text)',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        padding: 0,
                        lineHeight: 1
                      }}
                      aria-label="Delete price record"
                    >
                      ×
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            color: 'var(--text-secondary)',
            fontSize: '14px',
            fontStyle: 'italic',
            border: '2px dashed var(--border-light)',
            borderRadius: '4px',
            padding: '24px',
            textAlign: 'center'
          }}>
            No price history recorded
          </div>
        )}
      </div>
    </div>
  );
});

PriceHistoryManager.displayName = 'PriceHistoryManager';

export default PriceHistoryManager;
